import { useEffect } from 'react';

/**
 * App-wide guard against browser autofill on plain text/search fields.
 *
 * A browser is free to autofill any `<input>` that doesn't declare its intent,
 * and recent Chrome/Safari surface credit-card / address suggestions (Google
 * Pay) on generic text fields — search boxes, filters, combobox filters — which
 * looks unprofessional and risks leaking saved payment data into the wrong box.
 *
 * This marks every plain `type="text"` / `type="search"` input that does NOT
 * already declare `autocomplete` with `autocomplete="off"`. Fields that
 * legitimately want autofill (login, signup, password, credential wizard) set
 * their own `autocomplete`, so they are left untouched; `email`/`tel`/`number`/
 * `password` inputs are also left alone (they don't get credit-card autofill,
 * and their type-specific autofill is usually wanted). A MutationObserver keeps
 * this true for inputs mounted later (modals, route changes, portals).
 */
const AUTOFILLABLE_TYPES = new Set(['text', 'search']);

function suppress(el: Element): void {
  if (!(el instanceof HTMLInputElement)) return;
  if (el.hasAttribute('autocomplete')) return;
  const type = (el.getAttribute('type') || 'text').toLowerCase();
  if (!AUTOFILLABLE_TYPES.has(type)) return;
  el.setAttribute('autocomplete', 'off');
}

function scan(root: ParentNode): void {
  root.querySelectorAll?.('input').forEach(suppress);
}

export function useSuppressAutofill(): void {
  // DOM-level guard: there is no shared <Input> component to thread the
  // attribute through, and native autofill is decided by the browser off the
  // rendered attribute — so this reaches past React to set it, and observes
  // for inputs added after mount. Nothing else can express "every input,
  // including ones rendered later, unless it opted in."
  useEffect(() => {
    scan(document.body);
    const observer = new MutationObserver((records) => {
      for (const record of records) {
        record.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          suppress(node);
          scan(node);
        });
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);
}
